import { useEffect } from 'react';
import sanitizeDropdownValueForDataLayer from '../../common/utils/sanitizeDropdownValueForDataLayer';
import triggerGoogleTagManagerCustomEvent from '../../common/utils/triggerGoogleTagManagerCustomEvent';
import GtmEvents from '../constants/gtmEvents';
import findAssociatedLabel from '../utils/findAssociatedLabel';

function useGtmDropdownTracking() {
  useEffect(() => {
    // Handle native select change events
    const handleChange = (e: Event) => {
      if (!(e.target instanceof HTMLSelectElement)) {
        return;
      }

      // Use findAssociatedLabel to find the label for the select element
      const label = findAssociatedLabel(e.target);

      // For multi-select elements, selectedIndex may be -1, so we need to use selectedOptions
      const isMultiSelect = e.target.multiple;
      const selectedOption = isMultiSelect
        ? e.target.selectedOptions[0]
        : e.target.options[e.target.selectedIndex];
      const selectedIndex = isMultiSelect
        ? Array.from(e.target.options).indexOf(selectedOption)
        : e.target.selectedIndex;

      const value = sanitizeDropdownValueForDataLayer(
        e.target,
        selectedOption?.text || e.target.value,
      );

      triggerGoogleTagManagerCustomEvent(GtmEvents.DROPDOWN_SELECTED, {
        label,
        value,
        option_index: selectedIndex,
      });
    };

    // Handle clicks on our custom dropdown options (DropdownSelect, DropdownMultiSelect, etc.)
    const handleClick = (e: MouseEvent) => {
      if (!(e.target instanceof HTMLElement)) {
        return;
      }

      // Check if this is a click on a dropdown option
      const dropdownOption = e.target.closest('[role="option"]');
      if (!dropdownOption || !(dropdownOption instanceof HTMLElement)) {
        return;
      }

      // Get the associated select element by name
      const selectName = dropdownOption.closest('[role="combobox"]')?.getAttribute('data-name');
      if (!selectName) {
        return;
      }

      const selectElement = document.querySelector(
        `select[name="${selectName}"]`,
      ) as HTMLSelectElement;
      if (!selectElement) {
        return;
      }

      // Extract value and find its index in the original options
      const optionValue = dropdownOption.getAttribute('data-option-value');
      if (!optionValue) {
        return;
      }

      const options = Array.from(selectElement.options);
      const selectedIndex = options.findIndex((opt) => opt.value === optionValue);
      const selectedOption = options[selectedIndex];

      // Find label (check for aria-labelledby on the combobox)
      let label: string | undefined;
      const combobox = dropdownOption.closest('[role="combobox"]');
      const labelledById = combobox?.getAttribute('aria-labelledby');
      if (labelledById) {
        const labelElement = document.getElementById(labelledById);
        if (labelElement) {
          label = labelElement.textContent?.trim();
        }
      }

      const value = sanitizeDropdownValueForDataLayer(
        selectElement,
        selectedOption?.text || optionValue,
      );

      triggerGoogleTagManagerCustomEvent(GtmEvents.DROPDOWN_SELECTED, {
        label,
        value,
        option_index: selectedIndex,
      });
    };

    document.addEventListener('change', handleChange, { capture: true });
    document.addEventListener('click', handleClick, { capture: true });
    return () => {
      document.removeEventListener('change', handleChange, { capture: true });
      document.removeEventListener('click', handleClick, { capture: true });
    };
  }, []);
}

export default useGtmDropdownTracking;
